import { useEffect, useState } from 'react';
import {
  atualizarStatusCessao,
  excluirArquivoTitulosPendentes,
  listarDadosDocumentosPendentes,
  uploadTitulosPendentes
} from 'api/capitalFlow';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { StatusCessaoEnum } from 'components/Shared/Enums/StatusCessaoEnum';

export default function useModalEnviarDocumentosHook({
  open,
  onClose,
  dadosDetalhes,
  buscaDadosAtualizadosTela
}) {
  const [liberarBtnEnviarDocumentos, setLiberarBtnEnviarDocumentos] = useState(false);
  const [loadingTituloPendente, setLoadingTituloPendente] = useState(false);
  const [loadingProsseguir, setLoadingProsseguir] = useState(false);
  const [arquivosEnviados, setArquivosEnviados] = useState([]);
  const [titulosPendentes, setTitulosPendentes] = useState([]);
  const [idsArquivos, setIdsArquivos] = useState(new Set());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalAlerta, setModalAlerta] = useState(false);
  const [observacoes, setObservacoes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [arquivosUI, setArquivosUI] = useState([]);

  useEffect(() => {
    if (open) {
      buscarTitulosPendentes();
    } else {
      setLiberarBtnEnviarDocumentos(false);
      setIdsArquivos(new Set());
      setUploadProgress(0);
      setUploading(false);
      setObservacoes('');
      setArquivosUI([]);
    }
  }, [open]);

  const buscarTitulosPendentes = () => {
    setLoadingTituloPendente(true);
    listarDadosDocumentosPendentes(dadosDetalhes?.id)
      .then(response => {
        setTitulosPendentes(response.data);
      })
      .catch(() => openSnackbarMain('Ocorreu um erro ao buscar os documentos pendentes', 'error'))
      .finally(() => {
        setLoadingTituloPendente(false);
      });
  };

  const onUploadFiles = async (files, onProgress, uuidTitulo) => {
    if (!files?.length) return [];

    const allowed = ['pdf', 'zip', 'xml'];
    const invalid = files.find(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !allowed.includes(ext);
    });

    if (invalid) return [];

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      files.forEach(file => formData.append('arquivos[]', file));
      formData.append('uuid_cessoes_capital_flow_titulo', uuidTitulo);

      const resp = await uploadTitulosPendentes(formData, e => {
        const total = e.total ?? 0;
        const percent = total ? Math.round((e.loaded * 100) / total) : 0;
        setUploadProgress(percent);
        onProgress?.(percent);
      });

      const saved = resp?.data?.original?.arquivos ?? [];
      return saved;
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao enviar arquivos', 'error');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const excluirArquivoEnviado = async ({ NOME }, uuidTitulo) => {
    try {
      await excluirArquivoTitulosPendentes(uuidTitulo, NOME);
      removeControleArquivo(NOME);
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao excluir arquivo', 'error');
      throw err;
    }
  };

  const removeControleArquivo = nome => {
    setIdsArquivos(prev => {
      if (!prev.has(nome)) return prev;
      const next = new Set(prev);
      next.delete(nome);
      return next;
    });
  };

  const handleUploadedFilesChange = files => {
    setArquivosUI(files);
    const idsDone = files.filter(f => f.status === 'done').map(f => f.id);

    setIdsArquivos(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const id of idsDone) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  useEffect(() => {
    const temArquivo = Array.from(idsArquivos).length > 0;
    setLiberarBtnEnviarDocumentos(temArquivo || observacoes.trim().length > 0);
  }, [idsArquivos, observacoes]);

  const enviarDocumentosTitulos = () => {
    setLoadingProsseguir(true);
    const params = {
      uuid_cessao: dadosDetalhes?.id,
      cod_status_cessao: StatusCessaoEnum.EM_ANALISE,
      observacoes: observacoes
    };

    atualizarStatusCessao(params)
      .then(() => {
        mostrarAlertaSucesso();
        buscaDadosAtualizadosTela();
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao enviar os documentos.', 'error');
      })
      .finally(() => {
        onClose();
        setLoadingProsseguir(false);
      });
  };

  const mostrarAlertaSucesso = () => {
    setModalAlerta(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    if (reason === 'escapeKeyDown') return;
    onClose();
  };

  return {
    liberarBtnEnviarDocumentos,
    handleUploadedFilesChange,
    enviarDocumentosTitulos,
    excluirArquivoEnviado,
    loadingTituloPendente,
    setLoadingProsseguir,
    setTitulosPendentes,
    setArquivosEnviados,
    loadingProsseguir,
    arquivosEnviados,
    titulosPendentes,
    setObservacoes,
    uploadProgress,
    setModalAlerta,
    onUploadFiles,
    observacoes,
    modalAlerta,
    handleClose,
    arquivosUI,
    uploading
  };
}
